/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import {
  ObservableList,
  ObserveModes,
  toValues,
  ChangedEvent,
  Change,
  ApplicationError,
  ChangeOptions,
} from '@typescript-kit/core';
import { ViewComponentKey } from '../key/view-component.key';
import { ViewErrorKey } from '../key/view-error.key';
import { ComponentData, ComponentModel, ComponentFieldKey } from '../model/component.model';

export type SelectionMode = 'none' | 'single' | 'multiple';

export class SelectionModes {
  static readonly NONE: SelectionMode = 'none';
  static readonly SINGLE: SelectionMode = 'single';
  static readonly MULTIPLE: SelectionMode = 'multiple';
}

export interface ListData extends ComponentData {
  header?: ComponentData;
  items?: Iterable<ComponentData>;
  footer?: ComponentData;
  selectionMode?: SelectionMode;
  selectedItems?: ComponentModel[];
  scrollToSelectedItem?: boolean;
}

export class ListFieldKey {
  static readonly HEADER = 'header';
  static readonly ITEMS = 'items';
  static readonly FOOTER = 'footer';
  static readonly SELECTION_MODE = 'selectionMode';
  static readonly SELECTED_ITEMS = 'selectedItems';
  static readonly SCROLL_TO_SELECTED_ITEM = 'scrollToSelectedItem';
}

export class ListModel extends ComponentModel implements ListData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(ListFieldKey).map((key) => ListFieldKey[key])
  );

  constructor(data?: ListData, keys?: string[]) {
    super(data, keys || ListModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get header(): ComponentModel {
    return this.get(ListFieldKey.HEADER);
  }

  set header(value: ComponentModel) {
    this.set(ListFieldKey.HEADER, value);
  }

  get items(): ObservableList<ComponentModel> {
    return this.get(ListFieldKey.ITEMS);
  }

  set items(value: ObservableList<ComponentModel>) {
    this.set(ListFieldKey.ITEMS, value);
  }

  get footer(): ComponentModel {
    return this.get(ListFieldKey.FOOTER);
  }

  set footer(value: ComponentModel) {
    this.set(ListFieldKey.FOOTER, value);
  }

  get selectionMode(): SelectionMode {
    return this.get(ListFieldKey.SELECTION_MODE);
  }

  set selectionMode(value: SelectionMode) {
    this.set(ListFieldKey.SELECTION_MODE, value);
  }

  get selectedItems(): ComponentModel[] {
    return this.get(ListFieldKey.SELECTED_ITEMS);
  }

  set selectedItems(value: ComponentModel[]) {
    this.set(ListFieldKey.SELECTED_ITEMS, value);
  }

  get scrollToSelectedItem(): boolean {
    return this.get(ListFieldKey.SCROLL_TO_SELECTED_ITEM);
  }

  set scrollToSelectedItem(value: boolean) {
    this.set(ListFieldKey.SCROLL_TO_SELECTED_ITEM, value);
  }

  protected createChildrenIterable(): Iterable<ComponentModel> {
    const self = this;
    return {
      [Symbol.iterator]: function* createChildrenGenerator() {
        if (self.header) {
          yield self.header;
        }
        for (const item of self.items) {
          yield item;
        }
        if (self.footer) {
          yield self.footer;
        }
      }
    };
  }

  protected setupValues(values: ListData, exclusive: boolean): ListData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.LIST;
    }
    this.setupChild(ListFieldKey.HEADER, values, exclusive);
    this.setupItems(values, exclusive);
    this.setupChild(ListFieldKey.FOOTER, values, exclusive);
    this.setupSelectionMode(values, exclusive);
    this.setupSelectedItems(values, exclusive);
    return super.setupValues(values, exclusive) as ListData;
  }

  protected setupItems(values: ComponentData, exclusive: boolean): void {
    if (!exclusive && !(ListFieldKey.ITEMS in values)) {
      return;
    }
    if (!(values[ListFieldKey.ITEMS] instanceof ObservableList)) {
      if (!values[ListFieldKey.ITEMS]) {
        values[ListFieldKey.ITEMS] = new ObservableList();
      } else if (Array.isArray(values[ListFieldKey.ITEMS])) {
        values[ListFieldKey.ITEMS] = new ObservableList(values[ListFieldKey.ITEMS]);
      } else {
        const childArray = new Array<any>();
        for (const child of toValues(values[ListFieldKey.ITEMS])) {
          childArray.push(child);
        }
        values[ListFieldKey.ITEMS] = new ObservableList(childArray);
      }
    }
    super.setupChildren(ListFieldKey.ITEMS, values, exclusive);
  }

  protected setupSelectionMode(values: ListData, exclusive: boolean): void {
    if (!exclusive
      && !(ListFieldKey.SELECTION_MODE in values)
      && !(ListFieldKey.ITEMS in values)
    ) {
      return;
    }
    if (exclusive && !(ListFieldKey.SELECTION_MODE in values)) {
      values.selectionMode = SelectionModes.NONE;
      return;
    }
    const selectionMode = this.getSetupValue(values, ListFieldKey.SELECTION_MODE, exclusive);
    if (selectionMode === SelectionModes.SINGLE || selectionMode === SelectionModes.MULTIPLE) {
      const items = this.getSetupValue(values, ListFieldKey.ITEMS, exclusive);
      if (items.observeMode === ObserveModes.LOCAL) {
        items.observeMode = ObserveModes.FLAT;
      }
    }
  }

  protected setupSelectedItems(values: ListData, exclusive: boolean): void {
    if (!exclusive
      && !(ListFieldKey.SELECTION_MODE in values)
      && !(ListFieldKey.SELECTED_ITEMS in values)
    ) {
      return;
    }
    const items = this.getSetupValue(values, ListFieldKey.ITEMS, exclusive);
    const selectionMode = this.getSetupValue(values, ListFieldKey.SELECTION_MODE, exclusive);
    if (!(ListFieldKey.SELECTED_ITEMS in values)) {
      // exclusive || selection mode is changed
      if (exclusive) {
        // select active items
        switch (selectionMode) {
          case SelectionModes.NONE:
            values.selectedItems = [];
            break;
          case SelectionModes.SINGLE:
            const activeItem = items.firstValue((item) => item.isActive);
            values.selectedItems = activeItem ? [activeItem] : [];
            break;
          case SelectionModes.MULTIPLE:
            values.selectedItems = items.toArray().filter((item) => item.isActive);
            break;
        }
      } else if (selectionMode === SelectionModes.SINGLE && this.selectedItems.length > 1) {
        values.selectedItems = [this.selectedItems[0]];
      }
    } else if (!values.selectedItems) {
      values.selectedItems = [];
    } else {
      // verfiy selected items
      values.selectedItems.forEach((item, index) => {
        if (items.indexOf(item) < 0) {
          throw new ApplicationError({
            key: ViewErrorKey.SELECTED_ITEM_INVALID,
            arguments: { index }
          });
        }
      });
      if (selectionMode === SelectionModes.SINGLE && values.selectedItems.length > 1) {
        throw new ApplicationError({
          key: ViewErrorKey.MULTIPLE_SELECTED_ITEMS_NOT_ALLOWED
        });
      }
    }
  }

  protected onChangedValues(changes: { [key: string]: Change<any> }, options: ChangeOptions): void {
    // update isActive state of items after values are set
    if (this.selectionMode === SelectionModes.SINGLE || this.selectionMode === SelectionModes.MULTIPLE) {
      if (changes[ListFieldKey.SELECTION_MODE] && !changes[ListFieldKey.ITEMS]) {
        // update isActive state for all items if selection mode is changed
        if (this.selectedItems.length === 0) {
          this.items.forEach((item) => item.isActive = false);
        } else if (this.selectedItems.length === 1) {
          const selectedItem = this.selectedItems[0];
          this.items.forEach((item) => item.isActive = item === selectedItem);
        } else {
          const selectedItems = this.selectedItems;
          this.items.forEach((item) => item.isActive = selectedItems.indexOf(item) >= 0);
        }
      } else if (changes[ListFieldKey.SELECTED_ITEMS]) {
        // update isActive state for only for changed items
        const originalSelectedItems = changes[ListFieldKey.SELECTED_ITEMS].originalValue as ComponentModel[];
        const currentSelectedItems = changes[ListFieldKey.SELECTED_ITEMS].currentValue as ComponentModel[];
        if (originalSelectedItems) {
          originalSelectedItems.forEach((item) => item.isActive = currentSelectedItems.includes(item));
          currentSelectedItems.forEach((item) => item.isActive = true);
        } else {
          currentSelectedItems.forEach((item) => item.isActive = true);
        }
      }
    }
    // update isActive states, before changed event is fired
    super.onChangedValues(changes, options);
  }

  protected onChildrenPropertyChanged(key: string, event: ChangedEvent): void {
    super.onChildrenPropertyChanged(key, event);
    if (key === ListFieldKey.ITEMS && event.originalEvent !== event) {
      this.onItemPropertyChanged(event.originalEvent);
    }
  }

  protected onChildrenChanged(
    key: string,
    addedEntries: Array<[string, ComponentModel]>,
    removedEntries: Array<[string, ComponentModel]>,
    movedEntries: Array<[string, ComponentModel]>
  ): void {
    // update parent property before updating isActive states and deselecting removed items;
    super.onChildrenChanged(key, addedEntries, removedEntries, movedEntries);

    if (this.selectionMode === SelectionModes.SINGLE || this.selectionMode === SelectionModes.MULTIPLE) {
      // deactivate unselected items
      addedEntries.forEach((entry) => {
        const item = entry[1];
        if (!item.isActive) {
          return;
        }
        item.isActive = this.selectedItems.includes(item);
      });
    }
    // delelect removed items
    if (this.selectedItems.length > 0 && removedEntries.length > 0) {
      const selectedItems = this.selectedItems.filter((item) => !removedEntries.find((entry) => item === entry[1]));
      if (selectedItems.length !== this.selectedItems.length) {
        this.selectedItems = selectedItems;
      }
    }

  }

  protected onItemPropertyChanged(event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      return;
    }
    if (event.changes[ComponentFieldKey.IS_ACTIVE]) {
      const item = event.source as ComponentModel;
      switch (this.selectionMode) {
        case SelectionModes.SINGLE:
          if (item.isActive) {
            if (this.selectedItems.length !== 1 || this.selectedItems[0] !== item) {
              this.selectedItems = [item];
            }
          } else {
            if (this.selectedItems.length === 1 && this.selectedItems[0] === item) {
              this.selectedItems = [];
            }
          }
          break;
        case SelectionModes.MULTIPLE:
          const selectedIndex = this.selectedItems.indexOf(item);
          if (item.isActive) {
            if (selectedIndex < 0) {
              this.selectedItems = this.selectedItems.concat([item]);
            }
          } else {
            if (selectedIndex >= 0) {
              this.selectedItems = this.selectedItems.slice(0, selectedIndex)
                .concat(this.selectedItems.slice(selectedIndex + 1, this.selectedItems.length));
              // this.selectedItems = this.selectedItems.filter((selectedItem, index) => index !== selectedIndex);
            }
          }
          break;
      }
    }
  }

}
